import { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PreLaunchBanner from "@/components/redeem/PreLaunchBanner";
import RateTicker from "@/components/redeem/RateTicker";
import SessionState from "@/components/redeem/SessionState";
import RedemptionFormShell from "@/components/redeem/RedemptionFormShell";
import RecentRedemptions from "@/components/redeem/RecentRedemptions";
import { getAuthMe, postLogout } from "@/lib/api";

/**
 * AssosPay `/redeem` shell (AP-4D + AP-4E + AP-4F).
 *
 * Reads `/api/auth/me` once on mount, owns logout, and provides a
 * `refreshRecent` counter to RecentRedemptions so a successful submit
 * (AP-4F) can trigger a fresh list fetch even when the user comes
 * back via in-app navigation.
 */
export default function Redeem() {
  const [me, setMe] = useState(null);
  const [meLoading, setMeLoading] = useState(true);
  const [recentRefreshKey, setRecentRefreshKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const out = await getAuthMe();
        if (alive) setMe(out);
      } catch (e) {
        // 401 is the normal "not linked yet" path — not a hard error.
        if (alive) setMe(null);
      } finally {
        if (alive) setMeLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const linked = !!me?.arc_user_id;

  /**
   * Logout handler — owned by the page so it can clear local UI state
   * after the call. Never raises (server is idempotent: 200 even when
   * no session is present). After clearing, we refetch /api/auth/me to
   * confirm the cookie is gone — the fetch is expected to 401.
   */
  const handleLogout = async () => {
    try {
      await postLogout();
    } catch (_) {
      // Server error or transient network — proceed with local clear
      // anyway so the UI matches the user's intent. The next /me call
      // will tell us the truth.
    }
    setMe(null);
    try {
      const refreshed = await getAuthMe();
      // If the server somehow still has us logged in (shouldn't happen),
      // surface that — but never mark the user "linked" via stale state.
      setMe(refreshed);
    } catch (_) {
      setMe(null);  // 401 — expected post-logout.
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="redeem-page">
      <Header />

      {/* Subtle vignette for visual depth without competing with content. */}
      <div className="relative">
        <div className="spotlight absolute inset-0 -z-0 pointer-events-none" />

        <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          {/* ---- Page heading ---- */}
          <div className="mb-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF]">
              AssosPay · ArenaCoin
            </p>
            <h1 className="mt-1 font-display text-3xl sm:text-4xl font-black text-white">
              Redeem ARC
            </h1>
            <p className="mt-2 font-mono text-xs text-white/60 max-w-2xl">
              Convert ArenaCoin into Assos365 platform credit at the
              current ecosystem rate. This is the AssosPay redemption
              utility — not a trading venue.
            </p>
          </div>

          {/* ---- Pre-launch banner ---- */}
          <div className="mb-6">
            <PreLaunchBanner />
          </div>

          {/* ---- Top row: rate + session ---- */}
          <div className="grid gap-4 md:grid-cols-2 mb-6">
            <RateTicker />
            <SessionState me={me} loading={meLoading} onLogout={handleLogout} />
          </div>

          {/* ---- Form shell ---- */}
          <div className="mb-6">
            <RedemptionFormShell
              linked={linked}
              me={me}
              onSubmitted={() => setRecentRefreshKey((k) => k + 1)}
            />
          </div>

          {/* ---- Recent redemptions ---- */}
          <div className="mb-10">
            <RecentRedemptions linked={linked} refreshKey={recentRefreshKey} />
          </div>

          {/* ---- Help / status copy ---- */}
          <div data-testid="redeem-help-copy" className="rounded-md border border-white/5 bg-black/40 p-5">
            <h3 className="font-display text-xs uppercase tracking-[0.25em] text-white/85">
              How redemption works
            </h3>
            <ul className="mt-3 space-y-2 font-mono text-[11px] leading-relaxed text-white/55 list-disc pl-5">
              <li>
                The ecosystem rate is operator-managed. It is not a
                market price and not derived from any exchange feed.
              </li>
              <li>
                When you submit a redemption, the rate locks at submit.
                Future rate changes will not affect a submitted
                redemption.
              </li>
              <li>
                Assos365 may review redemptions before crediting your
                account. Status updates appear in the list above as
                they progress.
              </li>
              <li>
                AssosPay never holds your private keys, never moves ARC
                on your behalf, and never mutates your Assos365
                balance directly.
              </li>
            </ul>
            <p className="mt-3 font-mono text-[10px] text-white/40">
              Read-only shell · submit and link flows ship in AP-4E and AP-4F.
            </p>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
}
